import { AssignStatus } from "@/enum/list.enum";

/**
 * One membership the "Add to CRM" list picker can act on.
 *
 * Carries the **list-person** id, not just the list id: a CRM selection is resolved per
 * list person (`selection.listPersonIds`), and one prospect can sit on the same list only
 * once but on many lists at once.
 */
export interface AddToCrmListOption {
	listPersonId: number;
	listId: number;
	listName: string;
}

/** A membership as the lead-relationship payload reports it. */
export interface AddToCrmOtherList {
	listPersonId: number;
	listId: number;
	listName: string;
	assignStatus: string;
}

/**
 * `assignStatus` values meaning the prospect has left the list. Naming a Dataverse
 * marketing list after one of these would describe a list the lead is no longer on.
 */
const INACTIVE_ASSIGN_STATUSES: readonly string[] = [AssignStatus.ARCHIVED];

export function isActiveMembership(assignStatus: string | null | undefined): boolean {
	return !assignStatus || !INACTIVE_ASSIGN_STATUSES.includes(assignStatus);
}

/** The part of a `ListPerson` this module needs, kept structural so the util stays pure. */
export interface AddToCrmListPersonLike {
	id?: number | null;
	list?: { id?: number | null; name?: string | null } | null;
}

/**
 * The membership a surface is showing, as the modal's `currentList` prop wants it. `null`
 * for a lead on no list — the modal then hides the picker rather than offering an option it
 * cannot act on.
 */
export function toAddToCrmListOption(
	listPerson: AddToCrmListPersonLike | null | undefined
): AddToCrmListOption | null {
	if (!listPerson?.id || !listPerson.list?.id) {
		return null;
	}

	return {
		listPersonId: listPerson.id,
		listId: listPerson.list.id,
		listName: listPerson.list.name ?? "",
	};
}

/**
 * The picker's options: the membership the surface is already showing first, then the
 * prospect's other **active** memberships.
 *
 * The current one is never filtered by status — it is the thread the user is looking at,
 * and hiding it would leave the picker empty on the very surface that opened it. The
 * relationship payload excludes it from `otherLists` by design, so the two never collide;
 * the id de-dupe is a belt-and-braces against a payload that stops doing that.
 */
export function buildAddToCrmListOptions(
	current: AddToCrmListOption | null | undefined,
	otherLists: AddToCrmOtherList[] | null | undefined
): AddToCrmListOption[] {
	const options: AddToCrmListOption[] = current ? [current] : [];
	const seen = new Set<number>(options.map((option) => option.listPersonId));

	for (const membership of otherLists ?? []) {
		if (seen.has(membership.listPersonId) || !isActiveMembership(membership.assignStatus)) {
			continue;
		}

		seen.add(membership.listPersonId);
		options.push({
			listPersonId: membership.listPersonId,
			listId: membership.listId,
			listName: membership.listName,
		});
	}

	return options;
}

/**
 * Which option starts selected: the surface's own membership when it is offered, otherwise
 * the only option there is. With several options and no current membership the user picks
 * deliberately — guessing one would sync the lead under a list they never chose.
 */
export function pickDefaultListOption(
	options: AddToCrmListOption[],
	currentListPersonId: number | null | undefined
): AddToCrmListOption | null {
	const current = options.find((option) => option.listPersonId === currentListPersonId);

	if (current) {
		return current;
	}

	return options.length === 1 ? options[0] : null;
}
